import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, num, brl } from "./_shared";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const COLORS = ["#d4a24c", "#c48a3e", "#a06d2c", "#7a4e14", "#5a3a0e", "#412c0c"];

export default function AdminGeo() {
  const [g, setG] = useState(null);
  useEffect(() => { api.get("/admin/geo").then(r => setG(r.data)); }, []);

  const stateData = (g?.by_state || []).map(s => ({
    ...s,
    label: s.code || "?",
    revenue: (g?.revenue_by_state_cents?.[s.code] || 0) / 100,
  }));

  return (
    <div className="p-3 sm:p-6 lg:p-8" data-testid="admin-geo">
      <PageHeader title="Distribuição geográfica" subtitle="Usuários e receita por estado e cidade" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card title="Usuários por estado" kicker="Ranking" testid="geo-users-by-state">
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={stateData} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3"/>
                <XAxis type="number" stroke="#64748b" fontSize={11}/>
                <YAxis type="category" dataKey="label" stroke="#64748b" fontSize={11} width={60}/>
                <Tooltip contentStyle={{ background: "#0f1524", border: "1px solid #1e293b", color: "#e2e8f0" }}/>
                <Bar dataKey="users" fill="#d4a24c" radius={[0,6,6,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Distribuição percentual" kicker="Base atual" testid="geo-pie">
          <div className="h-72">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={stateData.slice(0,6)} dataKey="users" nameKey="label" cx="50%" cy="50%" outerRadius={90} label>
                  {stateData.slice(0,6).map((_, i) => <Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0f1524", border: "1px solid #1e293b", color: "#e2e8f0" }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Top 20 cidades" kicker="Por número de usuários" testid="geo-top-cities">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="py-2 pr-4">#</th>
                <th className="py-2 pr-4">Cidade</th>
                <th className="py-2 pr-4">UF</th>
                <th className="py-2 pr-4 text-right">Usuários</th>
              </tr>
            </thead>
            <tbody>
              {(g?.by_city || []).length === 0 && <tr><td colSpan="4" className="py-6 text-center text-slate-500">Sem dados de cidade cadastrados</td></tr>}
              {(g?.by_city || []).map((c, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="py-2 pr-4 text-slate-500">{i+1}</td>
                  <td className="py-2 pr-4 text-slate-100">{c.city || "—"}</td>
                  <td className="py-2 pr-4 text-slate-300">{c.state || "—"}</td>
                  <td className="py-2 pr-4 text-right text-[#d4a24c] font-semibold">{num(c.users)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card title="Receita por estado" kicker="Pagamentos confirmados" testid="geo-revenue">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="py-2 pr-4">UF</th>
                <th className="py-2 pr-4 text-right">Usuários</th>
                <th className="py-2 pr-4 text-right">Receita</th>
              </tr>
            </thead>
            <tbody>
              {stateData.length === 0 && <tr><td colSpan="3" className="py-6 text-center text-slate-500">Sem dados</td></tr>}
              {stateData.map((s, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="py-2 pr-4 text-slate-100">{s.label}</td>
                  <td className="py-2 pr-4 text-right text-slate-300">{num(s.users)}</td>
                  <td className="py-2 pr-4 text-right text-[#d4a24c]">{brl(s.revenue * 100)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      <div className="mt-4 text-xs text-slate-500">
        Dica: preencha cidade e UF no seu perfil (menu superior) para começar a povoar esta análise.
      </div>
    </div>
  );
}
